import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Robot, CheckCircle, Spinner, Sparkle, Users, 
  ChatCircle, Megaphone, Lightning, Info, Trash
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { aiTwinAPI } from '@/utils/api';

const brandVoiceOptions = [
  { id: 'casual', label: 'Casual', description: 'Friendly, conversational, relatable' },
  { id: 'expert', label: 'Expert', description: 'Authoritative, knowledgeable, confident' },
  { id: 'motivational', label: 'Motivational', description: 'Inspiring, energizing, empowering' },
  { id: 'luxury', label: 'Luxury', description: 'Premium, sophisticated, exclusive' },
  { id: 'friendly', label: 'Friendly', description: 'Warm, helpful, personable' },
  { id: 'professional', label: 'Professional', description: 'Polished, clear, business-focused' }
];

const contentToneOptions = [
  { id: 'educational', label: 'Educational', description: 'Teaching and sharing knowledge' },
  { id: 'entertaining', label: 'Entertaining', description: 'Fun, engaging, with personality' },
  { id: 'inspiring', label: 'Inspiring', description: 'Focus on possibility and success' },
  { id: 'direct', label: 'Direct', description: 'Straightforward, action-oriented' },
  { id: 'storytelling', label: 'Storytelling', description: 'Narrative-driven, relatable' }
];

const AITwinSettingsModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    business_niche: '',
    target_audience: '',
    brand_voice: '',
    content_tone: '',
    offer_focus: ''
  });
  const [isConfigured, setIsConfigured] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchSettings();
    }
  }, [isOpen]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await aiTwinAPI.getSettings();
      const data = response.data;
      setSettings({
        business_niche: data.business_niche || '',
        target_audience: data.target_audience || '',
        brand_voice: data.brand_voice || '',
        content_tone: data.content_tone || '',
        offer_focus: data.offer_focus || ''
      });
      setIsConfigured(data.configured);
    } catch (error) {
      console.error('Failed to fetch assistant settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await aiTwinAPI.updateSettings(settings);
      toast.success('Settings saved! Your assistant is now personalized.');
      setIsConfigured(true);
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to save settings');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Are you sure you want to reset your assistant settings?')) return;
    
    setSaving(true);
    try {
      await aiTwinAPI.resetSettings();
      setSettings({
        business_niche: '',
        target_audience: '',
        brand_voice: '',
        content_tone: '',
        offer_focus: ''
      });
      setIsConfigured(false);
      toast.success('Assistant settings reset');
    } catch (error) {
      toast.error('Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-2xl paygbot-modal-shell bg-slate-900 rounded-2xl shadow-2xl border border-white/10"
        >
          {/* Header */}
          <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-white/10 px-6 py-5 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                  <Robot size={24} className="text-white" weight="fill" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Customize Your AI Assistant</h2>
                  <p className="text-slate-400 text-sm">Tell your assistant about your business so it creates better content for you.</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <X size={20} className="text-white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner size={32} className="text-blue-400 animate-spin" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Info Banner */}
                <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
                  <div className="flex items-start space-x-3">
                    <Info size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-slate-300">
                      <p className="font-medium text-blue-400 mb-1">Make PayGBot your personal AI partner</p>
                      <p>Configure these settings to make all AI-generated content (scripts, hooks, emails, offers) match your unique business voice and audience.</p>
                    </div>
                  </div>
                </div>

                {/* Business Niche */}
                <div>
                  <label className="flex items-center space-x-2 text-white font-medium mb-2">
                    <Sparkle size={18} className="text-purple-400" />
                    <span>Business Niche</span>
                  </label>
                  <input
                    type="text"
                    value={settings.business_niche}
                    onChange={(e) => handleChange('business_niche', e.target.value)}
                    placeholder="e.g., Fitness for busy moms, Real estate investing, SaaS marketing"
                    data-testid="assistant-niche"
                    className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <p className="text-xs text-slate-500 mt-1">Be specific - this helps the AI understand your exact market</p>
                </div>

                {/* Target Audience */}
                <div>
                  <label className="flex items-center space-x-2 text-white font-medium mb-2">
                    <Users size={18} className="text-blue-400" />
                    <span>Target Audience</span>
                  </label>
                  <input
                    type="text"
                    value={settings.target_audience}
                    onChange={(e) => handleChange('target_audience', e.target.value)}
                    placeholder="e.g., Busy professionals aged 30-45, First-time home buyers, SaaS founders"
                    data-testid="assistant-audience"
                    className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Brand Voice */}
                <div>
                  <label className="flex items-center space-x-2 text-white font-medium mb-3">
                    <ChatCircle size={18} className="text-cyan-400" />
                    <span>Brand Voice</span>
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {brandVoiceOptions.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => handleChange('brand_voice', option.id)}
                        data-testid={`voice-${option.id}`}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          settings.brand_voice === option.id
                            ? 'bg-cyan-500/20 border-cyan-500 text-white'
                            : 'bg-white/5 border-white/10 text-slate-300 hover:border-white/30'
                        }`}
                      >
                        <div className="font-medium text-sm">{option.label}</div>
                        <div className="text-xs text-slate-500 mt-1">{option.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Content Tone */}
                <div>
                  <label className="flex items-center space-x-2 text-white font-medium mb-3">
                    <Megaphone size={18} className="text-amber-400" />
                    <span>Content Tone</span>
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {contentToneOptions.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => handleChange('content_tone', option.id)}
                        data-testid={`tone-${option.id}`}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          settings.content_tone === option.id
                            ? 'bg-amber-500/20 border-amber-500 text-white'
                            : 'bg-white/5 border-white/10 text-slate-300 hover:border-white/30'
                        }`}
                      >
                        <div className="font-medium text-sm">{option.label}</div>
                        <div className="text-xs text-slate-500 mt-1">{option.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Offer Focus */}
                <div>
                  <label className="flex items-center space-x-2 text-white font-medium mb-2">
                    <Lightning size={18} className="text-green-400" />
                    <span>Primary Offer to Promote</span>
                  </label>
                  <input
                    type="text"
                    value={settings.offer_focus}
                    onChange={(e) => handleChange('offer_focus', e.target.value)}
                    placeholder="e.g., My 6-week coaching program, The Ultimate Guide to X, Free consultation calls"
                    data-testid="assistant-offer"
                    className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <p className="text-xs text-slate-500 mt-1">The main product or service you want content to promote</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-slate-900/95 backdrop-blur border-t border-white/10 px-6 py-4">
            <div className="flex items-center justify-between">
              {isConfigured && (
                <button
                  onClick={handleReset}
                  disabled={saving}
                  data-testid="reset-assistant"
                  className="flex items-center space-x-2 px-4 py-2 text-red-400 hover:text-red-300 transition-colors"
                >
                  <Trash size={18} />
                  <span>Reset</span>
                </button>
              )}
              {!isConfigured && <div />}
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="px-5 py-2.5 text-slate-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-assistant"
                  className="flex items-center space-x-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium rounded-xl transition-all disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Spinner size={18} className="animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} weight="fill" />
                      <span>Save Settings</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AITwinSettingsModal;
